import { HttpApiError, HttpClientError } from "@effect/platform";
import {
  DatabaseError,
  DataIntegrityError,
  OpenSearchError,
} from "@redotech/effect-api-shared/auth/errors";
import { MessageSendBlockedError } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message/block-rule-error";
import { toast } from "@redotech/redo-web/alert";
import { Effect, ParseResult } from "effect";

class ApiError extends Error {
  constructor(
    message: string,
    readonly statusCode: number,
    readonly isRetryable: boolean,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

function showErrorToast(message: string) {
  toast(message, { variant: "error" });
}

type SupportTicketApiErrors =
  | HttpApiError.HttpApiDecodeError
  | HttpApiError.Unauthorized
  | HttpApiError.Forbidden
  | HttpApiError.NotFound
  | HttpApiError.BadRequest
  | HttpApiError.InternalServerError
  | HttpApiError.ServiceUnavailable
  | MessageSendBlockedError
  | DatabaseError
  | DataIntegrityError
  | OpenSearchError
  | HttpClientError.HttpClientError
  | ParseResult.ParseError;

export function catchApiErrors<A, R>(
  effect: Effect.Effect<A, SupportTicketApiErrors, R>,
): Effect.Effect<A, never, R> {
  return effect.pipe(
    Effect.catchTags({
      HttpApiDecodeError: () => {
        showErrorToast("Received an invalid response from the server");
        return Effect.die(new ApiError("Invalid API response", 400, false));
      },
      Unauthorized: () => {
        showErrorToast("You are not authorized to perform this action");
        return Effect.die(new ApiError("Unauthorized", 401, false));
      },
      Forbidden: () => {
        showErrorToast("You do not have permission to access this resource");
        return Effect.die(new ApiError("Forbidden", 403, false));
      },
      MessageSendBlockedError: (e) => {
        showErrorToast(e.blocked.message);
        return Effect.die(new ApiError(e.blocked.reason, 403, false));
      },
      NotFound: () => {
        showErrorToast("The requested resource was not found");
        return Effect.die(new ApiError("Not found", 404, false));
      },
      BadRequest: () => {
        showErrorToast("Invalid request. Please check your inputs.");
        return Effect.die(new ApiError("Bad request", 400, false));
      },
      InternalServerError: () => {
        showErrorToast("An internal server error occurred. Please try again.");
        return Effect.die(new ApiError("Internal server error", 500, true));
      },
      ServiceUnavailable: () => {
        showErrorToast(
          "The service is temporarily unavailable. Please try again.",
        );
        return Effect.die(new ApiError("Service unavailable", 503, true));
      },
      OpenSearchError: () => {
        showErrorToast("An internal server error occurred. Please try again.");
        return Effect.die(new ApiError("OpenSearch error", 500, true));
      },
      DatabaseError: (e) => {
        showErrorToast("A database error occurred. Please try again.");
        return Effect.die(
          new ApiError(e.message ?? "Database error", 500, true),
        );
      },
      DataIntegrityError: (e) => {
        showErrorToast("A data error occurred. Please contact support.");
        return Effect.die(
          new ApiError(e.message ?? "Data integrity error", 500, false),
        );
      },
      RequestError: () => {
        showErrorToast(
          "A network error occurred. Please check your connection.",
        );
        return Effect.die(new ApiError("Network error", 503, true));
      },
      ResponseError: () => {
        showErrorToast("The server returned an unexpected response");
        return Effect.die(new ApiError("Server error", 500, true));
      },
      ParseError: () => {
        showErrorToast("Failed to process server response");
        return Effect.die(new ApiError("Parse error", 500, false));
      },
    }),
  );
}

/**
 * Like catchApiErrors but suppresses toasts. Use inside React Query queryFn /
 * mutationFn so that the global QueryCache/MutationCache onError handler can
 * show a single toast after all retries are exhausted, instead of one per
 * attempt. The ApiError message carries the user-facing string so the cache
 * handler can surface it.
 */
export function catchApiErrorsSilent<A, R>(
  effect: Effect.Effect<A, SupportTicketApiErrors, R>,
): Effect.Effect<A, never, R> {
  return effect.pipe(
    Effect.catchTags({
      HttpApiDecodeError: () =>
        Effect.die(
          new ApiError(
            "Received an invalid response from the server",
            400,
            false,
          ),
        ),
      Unauthorized: () =>
        Effect.die(
          new ApiError(
            "You are not authorized to perform this action",
            401,
            false,
          ),
        ),
      Forbidden: () =>
        Effect.die(
          new ApiError(
            "You do not have permission to access this resource",
            403,
            false,
          ),
        ),
      MessageSendBlockedError: (e) =>
        Effect.die(new ApiError(e.blocked.message, 403, false)),
      NotFound: () =>
        Effect.die(
          new ApiError("The requested resource was not found", 404, false),
        ),
      BadRequest: () =>
        Effect.die(
          new ApiError(
            "Invalid request. Please check your inputs.",
            400,
            false,
          ),
        ),
      InternalServerError: () =>
        Effect.die(
          new ApiError(
            "An internal server error occurred. Please try again.",
            500,
            true,
          ),
        ),
      ServiceUnavailable: () =>
        Effect.die(
          new ApiError(
            "The service is temporarily unavailable. Please try again.",
            503,
            true,
          ),
        ),
      OpenSearchError: () =>
        Effect.die(
          new ApiError(
            "An internal server error occurred. Please try again.",
            500,
            true,
          ),
        ),
      DatabaseError: () =>
        Effect.die(
          new ApiError(
            "A database error occurred. Please try again.",
            500,
            true,
          ),
        ),
      DataIntegrityError: () =>
        Effect.die(
          new ApiError(
            "A data error occurred. Please contact support.",
            500,
            false,
          ),
        ),
      RequestError: () =>
        Effect.die(
          new ApiError(
            "A network error occurred. Please check your connection.",
            503,
            true,
          ),
        ),
      ResponseError: () =>
        Effect.die(
          new ApiError("The server returned an unexpected response", 500, true),
        ),
      ParseError: () =>
        Effect.die(
          new ApiError("Failed to process server response", 500, false),
        ),
    }),
  );
}
